import React, { useEffect, useRef, useState } from "react";
import { Send, Bot, User } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getSessionId() {
  let sid = localStorage.getItem("vp_chat_session");
  if (!sid) {
    sid = `s-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem("vp_chat_session", sid);
  }
  return sid;
}

export default function AIChatbot() {
  const [sessionId] = useState(getSessionId);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Szia! Volt Vince vagyok, a VargaVolt Kft. AI villanyszerelő asszisztense. Írd le pontosan, milyen villanyszerelési problémád van — segítek diagnosztizálni és tanácsot adni. ⚡",
    },
  ]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, streaming]);

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setStreaming(true);

    try {
      const res = await fetch(`${API}/ai/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });
      if (!res.body) throw new Error("No stream");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split("\n\n");
        buffer = events.pop() || "";
        for (const ev of events) {
          const line = ev.trim();
          if (!line.startsWith("data:")) continue;
          const data = line.slice(5).trim();
          try {
            const parsed = JSON.parse(data);
            if (parsed.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = {
                  ...copy[copy.length - 1],
                  content: copy[copy.length - 1].content + parsed.delta,
                };
                return copy;
              });
            }
            if (parsed.error) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: `⚠️ Hiba: ${parsed.error}` };
                return copy;
              });
            }
          } catch (e) {
            // ignore parse error
          }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          content: "⚠️ Nem sikerült kapcsolódni. Próbáld újra, vagy hívj minket közvetlenül.",
        };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div data-testid="ai-chatbot" className="border-2 border-[#262626] bg-black flex flex-col h-[560px]">
      <div className="flex items-center gap-3 border-b border-[#262626] px-4 py-3">
        <div className="w-3 h-3 bg-[#FFD700] animate-pulse" />
        <div className="font-mono text-xs uppercase tracking-widest text-[#FFD700]">
          terminal · volt_vince@villanypro:~$
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4 font-mono text-sm">
        <div className="text-[#A3A3A3]">{">"} session_id: {sessionId.slice(0, 24)}</div>
        <div className="text-[#A3A3A3]">{">"} model: gpt-5.2 · lang: hu_HU</div>
        <div className="text-[#A3A3A3]">{">"} loading_expert... <span className="text-[#FFD700]">OK</span></div>

        {messages.map((m, i) => (
          <div key={i} data-testid={`chat-msg-${i}`} className="flex gap-3">
            <div
              className={`w-7 h-7 shrink-0 flex items-center justify-center border ${
                m.role === "user" ? "bg-white text-black border-white" : "bg-[#FFD700] text-black border-[#FFD700]"
              }`}
            >
              {m.role === "user" ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
            </div>
            <div className="flex-1">
              <div className={`text-[10px] uppercase tracking-widest mb-1 ${m.role === "user" ? "text-white" : "text-[#FFD700]"}`}>
                {m.role === "user" ? "[ ÜGYFÉL ]" : "[ VOLT VINCE ]"}
              </div>
              <div className="whitespace-pre-wrap leading-relaxed text-[#F5F5F5]">
                {m.content || (streaming && i === messages.length - 1 ? "" : "")}
                {streaming && i === messages.length - 1 && (
                  <span className="cursor-blink" />
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="border-t border-[#262626] p-3 flex gap-2">
        <input
          data-testid="chat-input"
          className="input-brutal flex-1"
          placeholder="pl. nem világít a fürdőszobai lámpa, néha villog..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKey}
          disabled={streaming}
        />
        <button
          data-testid="chat-send"
          onClick={send}
          disabled={streaming || !input.trim()}
          className="btn-primary disabled:opacity-60 px-4"
        >
          <Send className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
